// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { t } from '@/i18n';

type NullableText = string | null | undefined;

type GameLike = {
  id?: NullableText;
  name?: NullableText;
  info?: NullableText;
  description?: NullableText;
};

type SubgameLike = {
  id?: NullableText;
  name?: NullableText;
  description?: NullableText;
};

function normalizeText(value: NullableText): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveI18nValue(
  key: string | null,
  backendValue: NullableText,
  explicitFallback?: NullableText
): string | null {
  if (key) {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  const backendText = normalizeText(backendValue);
  if (backendText) return backendText;

  const fallbackText = normalizeText(explicitFallback);
  if (fallbackText) return fallbackText;

  return null;
}

function gameKey(id: NullableText, field: 'name' | 'info' | 'description'): string | null {
  const normalizedId = normalizeText(id);
  if (!normalizedId) return null;
  return `games.definitions.${normalizedId}.${field}`;
}

function subgameKey(id: NullableText, field: 'name' | 'description'): string | null {
  const normalizedId = normalizeText(id);
  if (!normalizedId) return null;
  return `games.subgames.${normalizedId}.${field}`;
}

export function getGameDisplayName(game: GameLike | null | undefined, fallback?: NullableText): string {
  return resolveI18nValue(gameKey(game?.id, 'name'), game?.name, fallback) ?? '';
}

export function getGameDisplayInfo(game: GameLike | null | undefined, fallback?: NullableText): string {
  return resolveI18nValue(gameKey(game?.id, 'info'), game?.info, fallback) ?? '';
}

export function getGameDisplayDescription(game: GameLike | null | undefined, fallback?: NullableText): string {
  return resolveI18nValue(gameKey(game?.id, 'description'), game?.description, fallback) ?? '';
}

export function getSubgameDisplayName(subgame: SubgameLike | null | undefined, fallback?: NullableText): string {
  return resolveI18nValue(subgameKey(subgame?.id, 'name'), subgame?.name, fallback) ?? '';
}

export function getSubgameDisplayDescription(
  subgame: SubgameLike | null | undefined,
  fallback?: NullableText
): string {
  return resolveI18nValue(subgameKey(subgame?.id, 'description'), subgame?.description, fallback) ?? '';
}
